import type { CaptureResult, Properties } from 'posthog-js'

/**
 * Product analytics (PostHog). GA4 through GTM stays the ad-platform feed; this
 * is the funnel and session view, configured like the Upcomers apps:
 * explicit events only (no autocapture), no session recording (Clarity already
 * records), no exception capture (Faro owns errors).
 *
 * Production only, like Clarity: dev and staging traffic would otherwise land
 * in the one Exalted project and skew every funnel. The SDK is a dynamic import
 * like Faro's, so it never lands in the entry chunk and a disabled storefront
 * downloads none of it; events fired before it arrives are queued by
 * `utils/posthog`, which is the only way app code reaches PostHog.
 */
export default defineNuxtPlugin({
  name: 'posthog',
  parallel: true,
  setup() {
    // `posthog` is only declared in apps that opt in, so it is untyped for every
    // other app extending this layer - cast like the pixel plugins do.
    const config = useRuntimeConfig() as any
    const projectToken: string | undefined = config.public?.posthog?.projectToken
    const host: string | undefined = config.public?.posthog?.host
    const environment = config.public?.environment ?? config.public?.service?.environment

    if (!projectToken || !host || environment !== 'production')
      return

    queuePostHogCalls()

    import('posthog-js').then(({ default: posthog }) => {
      posthog.init(projectToken, {
        api_host: host,
        // Where PostHog's own links (toolbar, "view in PostHog") point. Only
        // differs from `api_host` once events go through a first-party proxy.
        ui_host: 'https://us.posthog.com',
        defaults: '2026-08-30',
        autocapture: false,
        disable_session_recording: true,
        capture_exceptions: false,
        before_send: stripUrlQueryStrings,
        loaded: () => setPostHogInstance(posthog),
      })
    }).catch(() => {
      // Analytics must never take the storefront down. A failed chunk load
      // (offline, blocked, stale deploy) just means no analytics.
      dropPostHogCalls()
    })

    // A logged-in customer is identified as soon as the deferred active-customer
    // check answers. Guests are identified from the order instead (see
    // `setCustomerForOrder` and `ecommerce.purchase`), and logging out resets in
    // `authLogout` - never here, since a session that expired between visits
    // also reads as "no customer". Absent on the error page, where `1.auth`
    // provides nothing.
    const { activeCustomer } = useAuth()
    if (activeCustomer)
      watch(() => activeCustomer.value?.id, customerId => identifyPostHogCustomer(customerId), { immediate: true })
  },
})

const URL_PROPERTY = /(?:url|referrer|href)$/i

/**
 * Keep origin + path only on every URL-like property, the rule Faro's
 * `redactUrlQueryStrings` applies here and the Upcomers apps apply to PostHog.
 * Query strings on this storefront carry e-mail addresses, password-reset and
 * verification tokens and coupon codes. Campaign parameters (utm_*, gclid,
 * fbclid, ...) are unaffected: posthog-js has already copied them into their
 * own properties before `before_send` runs.
 */
function stripUrlQueryStrings(event: CaptureResult | null): CaptureResult | null {
  if (!event)
    return null

  return {
    ...event,
    properties: stripProperties(event.properties),
    ...(event.$set && { $set: stripProperties(event.$set) }),
    ...(event.$set_once && { $set_once: stripProperties(event.$set_once) }),
  }
}

function stripProperties(properties: Properties): Properties {
  return Object.fromEntries(Object.entries(properties).map(([key, value]) => [
    key,
    typeof value === 'string' && URL_PROPERTY.test(key) ? value.split(/[?#]/, 1)[0] : value,
  ]))
}
